import { Component } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import {
  AuthRoles,
  ChildMeteringPoint,
  DataHubStatus,
  MeteringPointDetails,
  MeteringPointDto,
  MeteringPointsService
} from '@app/api/dmc';
import { Columns } from '@shared/interfaces/table/column-config';
import { locationDetailsMasterData } from '@shared/interfaces/dmc/masterdata/location-details.masterdata';
import { meteringpointDetailsMasterdata } from '@shared/interfaces/dmc/masterdata/meteringpoint-details.masterdata';
import { meterDetailsMasterdata } from '@shared/interfaces/dmc/masterdata/meter-details.masterdata';
import { CardComponent } from '@shared/components/card/card.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ValueDetailsComponent } from '@shared/components/value-details/value-details.component';
import { meteringPointMasterData } from '@shared/interfaces/dmc/masterdata/meteringpoint.masterdata';
import { DisconnectComponent } from '@shared/modals/meteringpoint/disconnect/disconnect.component';
import { ModalService } from '@app/core/services/modal.service';
import { HeaderService } from '@app/core/services/header.service';
import {
  MeteringPointOverviewActionMenu
} from '@views/meteringpoint/meteringpoint-overview/meteringpoint-overview.action-menu';
import { RootModalComponent } from '@shared/modals/meteringpoint/root-modal/root-modal.component';
import { ConnectModalComponent } from '@shared/modals/meteringpoint/connect-modal/connect-modal.component';
import { ReconnectModalComponent } from '@shared/modals/meteringpoint/reconnect-modal/reconnect-modal.component';
import {
  editMeteringPointModalConfig
} from '@shared/modals/meteringpoint/edit-meteringpoint-modal/edit-meteringpoint-modal-config';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { meteringPointChildTableConfig } from '@shared/table-configs/meteringpoint/meteringpoint-child.table';
import { TableComponent } from '@shared/components/table/table.component';
import { StateManagerService } from '@shared/state/state-manager.service';
import { hasRole } from '@app/core/guards/has-role.guard';
import {
  EditRejectedMeteringpointModalComponent
} from '@shared/modals/meteringpoint/edit-rejected-meteringpoint-modal/edit-rejected-meteringpoint-modal.component';


@Component({
  selector: 'app-meteringpoint-overview',
  imports: [
    CardComponent,
    TranslatePipe,
    ValueDetailsComponent,
    TableComponent,
  ],
  templateUrl: './meteringpoint-overview.component.html',
  styleUrl: './meteringpoint-overview.component.css'
})
export class MeteringpointOverviewComponent {
  /** ID **/
  public id?: string;

  /** Dto **/
  public meteringPoint?: MeteringPointDetails;
  public meteringPointDTO?: MeteringPointDto;

  /** Master Data **/
  public physicalMasterData: Columns<MeteringPointDetails>[] = meteringPointMasterData;
  public locationMasterData: Columns<MeteringPointDetails>[] = locationDetailsMasterData;
  public meteringPointMasterData: Columns<MeteringPointDetails>[] = meteringpointDetailsMasterdata;
  public meterMasterData: Columns<MeteringPointDetails>[] = meterDetailsMasterdata;

  /** Childrens Table **/
  public meteringPointChildTable: TableConfig<ChildMeteringPoint> = {
    ...meteringPointChildTableConfig,
    refreshable: true,
    refresh: () => this.fetchAndUpdateMeteringPoint(),
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly modalService: ModalService,
    private readonly headerService: HeaderService,
    private readonly meteringPointsService : MeteringPointsService,
    private readonly stateManagerService: StateManagerService,
  ) {
    this.route.paramMap.subscribe((q: ParamMap) => {
      this.id = q.get('id')!;

      // Fetch an update metering point
      this.fetchAndUpdateMeteringPoint();

      /** Header Action **/
      const mpWriter = hasRole([AuthRoles.MeteringPointWriter]);
      this.headerService.setActions(MeteringPointOverviewActionMenu,
        {
          edit: () => this.openEditModal(),
          rootdata: () => this.openRootModal(),
          connect: () => this.openConnectModal(),
          disconnect: () => this.openDisconnectModal(),
          reconnect: () => this.openReconnectModal(),
        },
        {
          edit: () => !mpWriter || (this.meteringPoint?.dataHubStatus === DataHubStatus.Rejected && this.meteringPointDTO?.existsInDataHub) || this.meteringPoint?.dataHubStatus === DataHubStatus.Pending || this.meteringPoint?.dataHubStatus === DataHubStatus.PendingJob,
          rootdata: () => !mpWriter,
          connect: () => !mpWriter || this.meteringPoint?.dataHubStatus === DataHubStatus.Rejected || this.meteringPoint?.dataHubStatus === DataHubStatus.Pending || this.meteringPoint?.dataHubStatus === DataHubStatus.PendingJob,
          disconnect: () => !mpWriter || this.meteringPoint?.dataHubStatus === DataHubStatus.Rejected || this.meteringPoint?.dataHubStatus === DataHubStatus.Pending || this.meteringPoint?.dataHubStatus === DataHubStatus.PendingJob,
          reconnect: () => !mpWriter || this.meteringPoint?.dataHubStatus === DataHubStatus.Rejected || this.meteringPoint?.dataHubStatus === DataHubStatus.Pending || this.meteringPoint?.dataHubStatus === DataHubStatus.PendingJob,
        }
      );
    });
  }

  public fetchAndUpdateMeteringPoint() {
    this.meteringPointsService.meteringPointsIdentificationGet(this.id!).subscribe((meteringPoint: MeteringPointDetails) => {
      this.meteringPoint = meteringPoint
    });

    this.stateManagerService.getMeteringPoint(this.id!).subscribe((meteringPoint: MeteringPointDto) => {
      this.meteringPointDTO = meteringPoint;
    });
  }

  public openDisconnectModal(): void {
    this.modalService.show({
      component: DisconnectComponent,
      title: 'common.disconnect',
      actions: [
        { type: 'primary', label: 'common.disconnect', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { id: this.meteringPoint?.identification }
    }).subscribe(() => {
      this.fetchAndUpdateMeteringPoint();
    });
  }

  public openConnectModal(): void {
    this.modalService.show({
      component: ConnectModalComponent,
      title: 'common.connect',
      actions: [
        { type: 'primary', label: 'common.connect', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { meteringPoint: this.meteringPoint },
    }).subscribe(() => {
      this.fetchAndUpdateMeteringPoint();
    });
  }


  public openEditModal(): void {
    if (this.meteringPointDTO?.existsInDataHub)
    {
      this.modalService.show({
        ...editMeteringPointModalConfig,
        params: { id: this.meteringPoint?.identification }
      }).subscribe(() => {
        this.fetchAndUpdateMeteringPoint();
      });
    } else {
      // If metering point doesn't exist in DataHub, resend create request
      this.modalService.show({
        component: EditRejectedMeteringpointModalComponent,
        title: 'meteringPoint.edit.title',
        size: 'xl',
        actions: [
          { type: 'primary', label: 'system.button.update', method: 'submit' },
          { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
        ],
        params: { meteringPointDTO: this.meteringPointDTO }
      }).subscribe(() => {
        this.fetchAndUpdateMeteringPoint();
      });
    }
  }

  public openRootModal(): void {
    this.modalService.show({
      component: RootModalComponent,
      title: 'meteringpoint.root.title',
      actions: [
        {type: 'primary', label: 'system.button.request', method: 'submit'},
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: { id: this.meteringPoint?.identification }
    }).subscribe(() => {
      this.fetchAndUpdateMeteringPoint();
    });
  }

  public openReconnectModal(): void {
    this.modalService.show({
      component: ReconnectModalComponent,
      title: 'meteringpoint.reconnect.title',
      actions: [
        {type: 'primary', label: 'meteringpoint.reconnect.action', method: 'submit'},
        {type: 'secondary', label: 'system.button.cancel', closeOnClick: true},
      ],
      params: { id: this.meteringPoint?.identification }
    }).subscribe(() => {
      this.fetchAndUpdateMeteringPoint();
    });
  }
}
